import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import type { ListData, ListItem } from '@/types';
import { embeddedListWeb, embeddedWeb } from './embeddedDesign';

interface EmbeddedListProps {
  listData: ListData;
}

function itemSearchText(item: ListItem): string {
  const metaText = item.meta
    ? Object.entries(item.meta)
        .map(([key, value]) => `${key} ${String(value ?? '')}`)
        .join(' ')
    : '';
  return [item.title, item.subtitle, item.description, item.status, metaText].filter(Boolean).join(' ').toLowerCase();
}

export function EmbeddedList({ listData }: EmbeddedListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filteredItems = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return listData.items;
    return listData.items.filter((item) => itemSearchText(item).includes(trimmed));
  }, [listData.items, query]);

  const showSearch = Boolean(listData.enableFiltering) && listData.items.length > 3;

  return (
    <div className={embeddedWeb.container}>
      <div className={embeddedWeb.header}>
        <div className={embeddedWeb.headerRow}>
          <div className="min-w-0">
            <h3 className={embeddedWeb.title}>{listData.title}</h3>
            {listData.description ? (
              <p className={embeddedWeb.description}>{listData.description}</p>
            ) : null}
          </div>

          {showSearch ? (
            <div className={embeddedWeb.searchWrap}>
              <Search className={embeddedWeb.searchIcon} />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('embedded.search.placeholder', 'Search...')}
                aria-label={`${t('embedded.search.ariaPrefix', 'Search')} ${listData.title}`}
                className={embeddedWeb.input}
              />
            </div>
          ) : null}
        </div>
      </div>

      {filteredItems.length > 0 ? (
        <div className={embeddedListWeb.itemWrap}>
          {filteredItems.map((item) => (
            <div key={item.id} className={embeddedListWeb.item}>
              <div className={embeddedListWeb.itemTop}>
                <div className={embeddedListWeb.itemBody}>
                  <p className={embeddedListWeb.itemTitle}>{item.title}</p>
                  {item.subtitle ? (
                    <p className={embeddedListWeb.itemSubtitle}>{item.subtitle}</p>
                  ) : null}
                </div>
                {item.status ? (
                  <span className={embeddedWeb.statusPill}>
                    {item.status}
                  </span>
                ) : null}
              </div>

              {item.description ? (
                <p className={embeddedListWeb.itemDescription}>{item.description}</p>
              ) : null}

              {item.meta && Object.keys(item.meta).length > 0 ? (
                <dl className={embeddedListWeb.meta}>
                  {Object.entries(item.meta).map(([key, value]) => (
                    <div key={key} className={embeddedListWeb.metaRow}>
                      <dt className={embeddedListWeb.metaKey}>{key}</dt>
                      <dd className={embeddedListWeb.metaValue}>{String(value ?? '')}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className={embeddedWeb.empty}>
          <p className={embeddedWeb.emptyTitle}>
            {t('embedded.empty.noMatchingRecords', 'No matching records')}
          </p>
          <p className={embeddedWeb.emptyText}>
            {listData.emptyMessage || t('embedded.empty.adjustSearch', 'Adjust the search and try again.')}
          </p>
        </div>
      )}
    </div>
  );
}
